import { FC, useMemo, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import { Play } from '../types';
import { PoolElement } from '../Pool';
import { PlayPlayer } from '../components/PlayPlayer';
import {
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  $selectedDetailsEvent,
  $selectedEvent,
  $plannerModalIsOpen,
  removeEvent,
} from '../store/planner';
import {
  DetailsOverlay,
  DetailsContainer,
  DetailsHeader,
  DetailsTitle,
  DetailsCloseButton,
  DetailsContent,
  DetailsBody,
  DetailsFooter,
  EditButton,
  DetailsDeleteButton,
  TypeBadge,
  FieldGroup,
  FieldLabel,
  FieldValue,
  DateTimeRow,
  Divider,
  SectionHeading,
  PlayerListReadonly,
  PlayerItemReadonly,
  PlayerNumberBadge,
  PlayerNameReadonly,
  PlayerFlagLabel,
  RoleBadge,
  EmptyState,
  PlayCardList,
  PlayCard,
  PlayCardPoolWrapper,
  PlayCardName,
} from './EventDetailsModal.style';
import type { PlannerEvent } from '../store/planner';

const { confirm } = Modal;

const typeColorMap: Record<string, string> = {
  training: '#66bb6a',
  match: '#ef5350',
  other: '#ffeb3b',
};

const typeLabelMap: Record<string, string> = {
  training: 'Training',
  match: 'Match',
  other: 'Other',
};

export const EventDetailsModal: FC = () => {
  const event = useStore($selectedDetailsEvent);
  const [playToPreview, setPlayToPreview] = useState<Play | null>(null);

  const typeColor = useMemo(
    () => (event ? typeColorMap[event.planType] || '#fff' : '#fff'),
    [event],
  );

  const handleClose = () => {
    $selectedDetailsEvent.set(null);
  };

  const handleEdit = () => {
    if (event) {
      $selectedEvent.set(event);
      $selectedDetailsEvent.set(null);
      setTimeout(() => {
        $plannerModalIsOpen.set(true);
      }, 200);
    }
  };

  const handleDelete = () => {
    if (!event || !event.id) return;

    confirm({
      title: 'Delete Event',
      content: `Are you sure you want to delete "${event.title}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        removeEvent(event.id as string);
        handleClose();
      },
    });
  };

  const formatDate = (date: any) => {
    if (!date) return null;
    return dayjs(date.toString()).format('YYYY-MM-DD');
  };

  const formatTime = (date: any) => {
    if (!date) return null;
    return dayjs(date.toString()).format('HH:mm');
  };

  if (!event) return null;

  return (
    <>
      <DetailsOverlay $isOpen={event != null}>
        <DetailsContainer>
          <DetailsHeader>
            <DetailsTitle>{event.title || 'Event Details'}</DetailsTitle>
            <DetailsCloseButton onClick={handleClose}>
              <div>
                <XMarkIcon
                  style={{ color: '#fff', height: '30px', width: '30px' }}
                />
              </div>
            </DetailsCloseButton>
          </DetailsHeader>

          <DetailsContent>
            <DetailsBody>
              {/* Type Badge */}
              <div>
                <TypeBadge $color={typeColor}>
                  {typeLabelMap[event.planType] || event.planType}
                </TypeBadge>
              </div>

              {/* Date & Time */}
              <DateTimeRow>
                {event.start != null && (
                  <FieldGroup>
                    <FieldLabel>Start</FieldLabel>
                    <FieldValue
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <CalendarIcon style={{ width: 18, height: 18 }} />
                      {formatDate(event.start)}
                      {!event.allDay && (
                        <>
                          <ClockIcon
                            style={{ width: 18, height: 18, marginLeft: 8 }}
                          />
                          {formatTime(event.start)}
                        </>
                      )}
                    </FieldValue>
                  </FieldGroup>
                )}
                {event.end != null && (
                  <FieldGroup>
                    <FieldLabel>End</FieldLabel>
                    <FieldValue
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <CalendarIcon style={{ width: 18, height: 18 }} />
                      {formatDate(event.end)}
                      {!event.allDay && (
                        <>
                          <ClockIcon
                            style={{ width: 18, height: 18, marginLeft: 8 }}
                          />
                          {formatTime(event.end)}
                        </>
                      )}
                    </FieldValue>
                  </FieldGroup>
                )}
              </DateTimeRow>

              {/* Description */}
              {event.description && (
                <FieldGroup>
                  <FieldLabel>Description</FieldLabel>
                  <FieldValue>{event.description}</FieldValue>
                </FieldGroup>
              )}

              {/* Training-specific fields */}
              {event.planType === 'training' && (
                <>
                  <Divider />
                  <SectionHeading>Training Details</SectionHeading>

                  {event.trainingDuration != null && (
                    <FieldGroup>
                      <FieldLabel>Duration</FieldLabel>
                      <FieldValue>{event.trainingDuration} min</FieldValue>
                    </FieldGroup>
                  )}

                  {event.trainingTypes && event.trainingTypes.length > 0 && (
                    <FieldGroup>
                      <FieldLabel>Training Type</FieldLabel>
                      <FieldValue>{event.trainingTypes.join(', ')}</FieldValue>
                    </FieldGroup>
                  )}
                </>
              )}

              {/* Match-specific fields */}
              {event.planType === 'match' && (
                <>
                  <Divider />
                  <SectionHeading>Match Details</SectionHeading>

                  {/* Players */}
                  <FieldGroup>
                    <FieldLabel>Players</FieldLabel>
                    {event.players && event.players.length > 0 ? (
                      <PlayerListReadonly>
                        {[...event.players]
                          .sort((a, b) => {
                            const roleOrder = (r?: string) =>
                              r === 'str' ? 0 : 1;
                            return roleOrder(a.role) - roleOrder(b.role);
                          })
                          .map((player) => (
                            <PlayerItemReadonly
                              key={player.id}
                              $role={player.role}
                            >
                              <PlayerNumberBadge $role={player.role}>
                                {player.number}
                              </PlayerNumberBadge>
                              <PlayerNameReadonly>
                                <span>{player.name}</span>
                                {player.flag && (
                                  <PlayerFlagLabel>
                                    {player.flag}
                                  </PlayerFlagLabel>
                                )}
                              </PlayerNameReadonly>
                              {player.role && (
                                <RoleBadge $role={player.role}>
                                  {player.role === 'str' ? 'STR' : 'SUB'}
                                </RoleBadge>
                              )}
                            </PlayerItemReadonly>
                          ))}
                      </PlayerListReadonly>
                    ) : (
                      <EmptyState>No players assigned</EmptyState>
                    )}
                  </FieldGroup>

                  {event.competitionType && (
                    <FieldGroup>
                      <FieldLabel>Competition Type</FieldLabel>
                      <FieldValue>{event.competitionType}</FieldValue>
                    </FieldGroup>
                  )}

                  {event.matchPlan && (
                    <FieldGroup>
                      <FieldLabel>Match Plan</FieldLabel>
                      <FieldValue>{event.matchPlan}</FieldValue>
                    </FieldGroup>
                  )}
                </>
              )}

              {/* Plays section — match & training only */}
              {event.planType !== 'other' &&
                event.plays &&
                event.plays.length > 0 && (
                  <>
                    <Divider />
                    <SectionHeading>Plays</SectionHeading>
                    <PlayCardList>
                      {event.plays.map((play, index) => (
                        <PlayCard
                          key={play.id || index}
                          onClick={() => setPlayToPreview(play)}
                        >
                          <PlayCardPoolWrapper>
                            {play.snapshots && play.snapshots.length > 0 && (
                              <PoolElement
                                halfView={true}
                                poolMaxHeight={75}
                                poolMaxWidth={130}
                                snapshot={play.snapshots[0]}
                                isStatic={true}
                                isDrawingEnabled={false}
                                drawingMode={0}
                                shouldCleanDrawings={false}
                                onDrawingsCleaned={() => {}}
                                shouldUndoLastDrawing={false}
                                onLastDrawingUndone={() => {}}
                                opponentIncrease={100}
                              />
                            )}
                          </PlayCardPoolWrapper>
                          <PlayCardName>{play.name}</PlayCardName>
                        </PlayCard>
                      ))}
                    </PlayCardList>
                  </>
                )}

              {/* Notes — always at bottom */}
              {event.note && (
                <>
                  <Divider />
                  <FieldGroup>
                    <FieldLabel>Notes</FieldLabel>
                    <FieldValue>{event.note}</FieldValue>
                  </FieldGroup>
                </>
              )}

              <div style={{ minHeight: '30px' }}></div>
            </DetailsBody>
          </DetailsContent>

          <DetailsFooter>
            <DetailsDeleteButton onClick={handleDelete}>
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <TrashIcon style={{ width: 18, height: 18 }} />
                Delete
              </span>
            </DetailsDeleteButton>
            <div style={{ flex: 1 }} />
            <EditButton onClick={handleEdit}>
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <PencilSquareIcon style={{ width: 18, height: 18 }} />
                Edit
              </span>
            </EditButton>
          </DetailsFooter>
        </DetailsContainer>
      </DetailsOverlay>

      {playToPreview && (
        <PlayPlayer
          play={playToPreview}
          isOpen={!!playToPreview}
          onClose={() => setPlayToPreview(null)}
        />
      )}
    </>
  );
};
